import 'App.css';

import React, {useState, useEffect, useRef} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import ProductDrawer from 'components/ProductDrawer';
import ProductGrid from 'components/ProductGrid';
import ProductList from 'components/ProductList';

import useCheckPC from 'hooks/useCheckPC';
import Loading from 'components/Loading';
import Header from 'components/Header';
import Footer from 'components/Footer';
import FloatingNav from 'components/FloatingNav';
import useLayout from 'hooks/useLayout';

import 'styles/font.css';
import 'styles/reset.css';

import useProducts from 'hooks/useProducts';
import LoginPage from 'components/LoginPage';

function App() {
  const { isPC, isMobile, isTabletSmall, isTabletLarge } = useCheckPC();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const prevProductRef = useRef(null);

  const [selectedFilter, setSelectedFilter] = useState(searchParams.get('filter') ?? 'All');
  const [selectedSort, setSelectedSort] = useState(searchParams.get('sort') ?? 'Default');

  /** shuffle Icon 클릭 시, load 애니메이션 적용시켜주는 state */
  const [isShuffled, setIsShuffled] = useState(false);

  const auth = localStorage.getItem('auth') ?? '';
  const search = searchParams.get('search') ?? '';

  const {
    view,
    isLoading,
    isDrawerOpen,
    openDrawer,
    closeDrawer,
    handleShowLoading,
    handleVisibleFooter,
    resetLayout,
    resetUserLayout,
  } = useLayout();

  const { shuffledData, allData, handleShuffleProducts } = useProducts();

  /** 새로고침 */
  useEffect(() => {
    handleShowLoading();
    handleVisibleFooter(900);
    if (window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (searchParams.get('product') || searchParams.get('lookbook')) {
      const currentProduct = searchParams.get('product')
          ? `${searchParams.get('product')}-${searchParams.get('color')}`
          : searchParams.get('lookbook');
      prevProductRef.current = currentProduct;

      openDrawer();
    } else {
      const sort = searchParams.get('sort');
      const productCardId = sort ? `${prevProductRef.current}-sorted` : prevProductRef.current;

      closeDrawer();

      const isLookbookFilter = searchParams.get('filter') === 'Lookbook';
      const shouldScroll =
          (!isLookbookFilter &&
              (view !== 'TEXT' || (isTabletSmall || isMobile)));

      if (shouldScroll) {
        setTimeout(() => {
          const element = document.getElementById(productCardId);
          if (element) {
            element.scrollIntoView({ block: 'center' });
          }
        }, 200);
      }
    }

    setSelectedFilter(searchParams.get('filter') ?? 'All');
    setSelectedSort(searchParams.get('sort') ?? 'Default');
  }, [searchParams]);

  useEffect(() => {
    if (shuffledData.length > 0) {
      handleShuffleProducts(shuffledData, view);
    }
  }, [isShuffled]);

  /** 초기화를 시켜주는 함수 */
  const handleInitClick = () => {
    window.scrollTo(0, 0);
    resetLayout();
    resetUserLayout();
    navigate('/');
    handleShowLoading();
    setIsShuffled(false);
  };

  useEffect(() => {
    if (shuffledData.length !== 0) {
      const safeAreaEl = document.querySelector('#safe-area');
      if ((isMobile || isTabletSmall) && isDrawerOpen) {
        document.querySelector('#root').style.overflow = 'hidden';
        document.querySelector('#root').style.height = '100dvh';
      } else {
        document.querySelector('#root').style.overflow = 'auto';
        document.querySelector('#root').style.height = 'auto';
      }
      if (isPC) {
        if (isDrawerOpen) {
          document.querySelector('main').style.minHeight = 'calc(100dvh - 40.8rem)';
        } else {
          document.querySelector('main').style.minHeight = 'calc(100dvh - 31.3rem)';
        }
      }
      if (safeAreaEl) {
        if (isTabletSmall || isTabletLarge) {
          document.querySelector('#safe-area').style.width = '100vw';
        } else {
          document.querySelector('#safe-area').style.width = '0';
        }
      }
    }
  }, [isDrawerOpen, isPC, isMobile, isTabletSmall, shuffledData]);

  const handleDrawerClose = () => {
    handleVisibleFooter();

    let productCardId = searchParams.get('product')
      ? `${searchParams.get('product')}-${searchParams.get('color')}`
      : searchParams.get('lookbook');

    if (searchParams.get('sort')) productCardId += '-sorted';
    const newSearchParams = {};
    if (search) newSearchParams['search'] = search;
    if (searchParams.get('filter')) newSearchParams['filter'] = searchParams.get('filter');
    if (searchParams.get('sort')) newSearchParams['sort'] = searchParams.get('sort');

    setSearchParams(newSearchParams);
  };

  if (auth === '' && shuffledData.length === 0) {
    return <LoginPage handleShowLoading={handleShowLoading} />;
  }

  return (
    <>
      {isLoading && <Loading />}
      <Header handleInitClick={handleInitClick} selectedFilter={selectedFilter} selectedSort={selectedSort} />
      <main
          style={{
          width: `${
            (isPC || isTabletLarge) && isDrawerOpen && view === 'IMAGE' ? 'calc(50% - 1.8rem)' : 'calc(100% - 2.4rem)'
          }`,
        }}
      >
        {shuffledData.length === 0 && <p className={'no-result'}>No search results found.</p>}
        {shuffledData.length > 0 && view === 'IMAGE' && (
          <ProductGrid
            allData={allData}
            products={shuffledData}
            filter={selectedFilter}
            sort={selectedSort}
            isShuffled={isShuffled}
          />
        )}
        {shuffledData.length > 0 && view === 'TEXT' && (
          <ProductList allData={allData} products={shuffledData} filter={selectedFilter} sort={selectedSort} />
        )}
      </main>
      <Footer />
      {shuffledData.length > 0 && isDrawerOpen && (
        <ProductDrawer allData={allData} products={shuffledData} handleClose={handleDrawerClose} />
      )}
      {((isMobile || isTabletSmall) && isDrawerOpen) || (
          <FloatingNav isShuffled={isShuffled} setIsShuffled={setIsShuffled} />
      )}
    </>
  );
}

export default App;
